import { FC, memo, useCallback, useMemo } from 'react';
import { FaCheck } from 'react-icons/fa6';
import Image from 'next/image';

import { AuthIterationType } from '../types';
import { sophiPaywallCopyMap, EdgeLogoSvg } from '.';
import { useEdgeCheckoutMode, useIsUserLoggedIn } from '@benzinga/user-context';
import { usePaywallTracking } from '../../../hooks/usePaywallTracking';
import { useEdgePaywallCheckoutModal } from '@benzinga/edge';
import { buildCheckoutUrl, getPaywallConfig, handlePaywallLoginRedirect } from '../../../utils/paywallUtils';
import styles from '../paywall.module.scss';

interface OptimizelyPaywallProps extends AuthIterationType {
  isMobile?: boolean;
  paywallId?: string;
}

export const OptimizelyPaywall: FC<OptimizelyPaywallProps> = memo(
  ({ allowClose, isMobile, placement, setShowPaywall }) => {
    const adType = allowClose ? 'soft' : 'hard';
    const adScreen = isMobile ? 'mo' : 'de';
    const adId = `${adType}-${adScreen}-optimizely`;
    const paywallId = 'ct-pw1';
    const { hasAltCheckout, paywallCopy, utmSource } = getPaywallConfig(
      paywallId,
      sophiPaywallCopyMap,
      'optimizely',
      'optimizely',
    );

    const campaign = 'command-center-optimizely';

    const checkoutUrl = buildCheckoutUrl({
      ad: adId,
      adType: 'paywall',
      campaign,
      hasAltCheckout,
    });

    const checkoutParams = useMemo(
      () => ({
        ad: adId,
        adType: 'paywall',
        campaign,
      }),
      [adId, campaign],
    );

    const listItems = paywallCopy?.listItems ?? [];
    const isLoggedIn = useIsUserLoggedIn();
    const { checkoutMode, isCheckoutModeReady } = useEdgeCheckoutMode();

    const { handleCheckoutClick, usesModalCheckout } = useEdgePaywallCheckoutModal({
      checkoutMode,
      checkoutParams,
      checkoutType: 'article_paywall_iframe',
      checkoutUrl,
      isCheckoutModeReady,
      paywallId: utmSource,
      placement,
      setShowPaywall,
    });

    usePaywallTracking({
      allowClose,
      checkoutUrl,
      placement,
      utmSource,
    });

    const handleLoginRedirect = useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        handlePaywallLoginRedirect(utmSource);
      },
      [utmSource],
    );

    const paywallBody = (
      <>
        <div className={styles['paywall-logo-wrapper']}>
          <EdgeLogoSvg />
          <span>exclusive</span>
        </div>
        <div className={styles['content-wrapper']}>
          {paywallCopy?.title && <h2 className={styles['paywall-title']}>{paywallCopy.title}</h2>}
          <div className={styles['paywall-list-wrapper']}>
            {paywallCopy?.listHeading && <h3 className={styles['paywall-list-heading']}>{paywallCopy.listHeading}</h3>}
            <ul className={styles['paywall-list']}>
              {listItems.map((item, index) => (
                <li className={styles['paywall-list-item']} key={index}>
                  <FaCheck className={styles['paywall-check-icon']} size={9} />
                  <div>{item}</div>
                </li>
              ))}
            </ul>
            <div className={styles['paywall-button']}>
              {isMobile && paywallCopy?.buttonMobile ? paywallCopy.buttonMobile : paywallCopy?.button}
            </div>
          </div>
        </div>
        {!isLoggedIn && (
          <div className={styles['auth-redirect-footer']} onClick={handleLoginRedirect}>
            Already a member? <u>Log in</u>
          </div>
        )}
        <div className="absolute bottom-0 w-full flex flex-row justify-between">
          <Image
            alt="poly bear"
            className={styles['desktop-animal']}
            height={798}
            src="/next-assets/images/paywalls/poly-bear.png"
            unoptimized
            width={524}
          />
          <Image
            alt="poly bull"
            className={styles['desktop-animal']}
            height={798}
            src="/next-assets/images/paywalls/poly-bull.png"
            unoptimized
            width={524}
          />
        </div>
      </>
    );

    const handlePaywallCheckoutClick = useCallback(
      (event: React.MouseEvent) => {
        if (!isCheckoutModeReady) return;
        event.preventDefault();
        handleCheckoutClick();
      },
      [handleCheckoutClick, isCheckoutModeReady],
    );

    const paywallLinkClassName = 'w-full flex flex-col overflow-hidden paywall-link';

    return (
      <>
        <div className="fixed inset-0 z-40 touch-none pointer-events-none"></div>
        <div
          className={`paywall-wrapper paywall-content w-full max-h-[100vh] h-auto animate-fade-up animate-delay-300 animate-once no-scrollbar overflow-scroll flex flex-col md:flex-row items-center md:items-start justify-start md:justify-center md:gap-12 fixed z-50 bottom-0 left-0 bg-white border-t ${styles['paywall-wrapper']}`}
        >
          {usesModalCheckout ? (
            <div
              className={`${paywallLinkClassName} cursor-pointer`}
              onClick={handleCheckoutClick}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleCheckoutClick();
                }
              }}
              role="button"
              tabIndex={0}
            >
              {paywallBody}
            </div>
          ) : (
            <a
              className={paywallLinkClassName}
              href={checkoutUrl}
              onClick={handlePaywallCheckoutClick}
              rel="noopener noreferrer"
              target="_blank"
            >
              {paywallBody}
            </a>
          )}
        </div>
      </>
    );
  },
);

OptimizelyPaywall.displayName = 'OptimizelyPaywall';
